/**
 * App.jsx - Main Application Router
 * Il Diario di Bordo - Frontend
 * 
 * FASE 5: Aggiunto supporto per ruolo Responsabile e routes Organization
 * FASE 3: Aggiunto LegalSignPage per firma documenti legali
 * 
 * FIX: Redirect automatico Responsabile a /organization invece di /dashboard
 */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { Loader2 } from 'lucide-react'

// Layouts
import AppLayout from './components/layouts/AppLayout'

// Pages - Auth
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import AcceptInvitePage from './pages/AcceptInvitePage'
import TwoFactorVerifyPage from './pages/auth/TwoFactorVerifyPage'
import TwoFactorSetupPage from './pages/auth/TwoFactorSetupPage'
import TwoFactorRecoveryPage from './pages/auth/TwoFactorRecoveryPage'
import VerifyEmailPage from './pages/auth/VerifyEmailPage'
import ResendVerificationPage from './pages/auth/ResendVerificationPage'

// Pages - App
import DashboardPage from './pages/DashboardPage'
import MinoriPage from './pages/minori/MinoriPage'
import MinoreDetailPage from './pages/minori/MinoreDetailPage'
import NewMinorePage from './pages/minori/NewMinorePage'
import EntriesPage from './pages/entries/EntriesPage'
import NewEntryPage from './pages/entries/NewEntryPage'
import UsersPage from './pages/users/UsersPage'
import SettingsPage from './pages/settings/SettingsPage'
import ReportsPage from './pages/reports/ReportsPage'
import TemplateBuilderPage from './pages/reports/TemplateBuilderPage'
import ProfilePage from './pages/ProfilePage'
import SubscriptionPage from './pages/SubscriptionPage'
import TrashPage from './pages/trash/TrashPage'
import CheckinPage from './pages/CheckinPage'
import GroupActivityPage from './pages/GroupActivityPage'

// FASE 3: Legal Documents
import LegalSignPage from './pages/LegalSignPage'

// Centro Notifiche + Chat + Reminder
import NotificationsPage from './pages/NotificationsPage'
import MessagesPage from './pages/MessagesPage'
import RemindersPage from './pages/RemindersPage'
import { NotificationFeedProvider } from './hooks/useNotificationFeed'

// FASE 5: Pages - Organization (Responsabile)
import ResponsabileDashboard from './pages/organization/ResponsabileDashboard'
import CentersPage from './pages/organization/CentersPage'
import CreateCenterPage from './pages/organization/CreateCenterPage'
import CenterDetailPage from './pages/organization/CenterDetailPage'
import OrganizationSettingsPage from './pages/organization/OrganizationSettingsPage'
import OrganizationContracts from './pages/organization/OrganizationContracts'
import OrganizationBilling from './pages/organization/OrganizationBilling'
import OrganizationExport from './pages/organization/OrganizationExport'

/**
 * Protected Route wrapper
 * Gestisce:
 * - Autenticazione
 * - Race condition dopo login
 * - Redirect a setup 2FA per coordinatori/responsabili
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, needs2FASetup, user } = useAuth()
  const location = useLocation()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Redirect a setup 2FA se necessario (coordinatori e responsabili)
  // Escludiamo la pagina di setup stessa per evitare loop
  if (needs2FASetup && (user?.role === 'coordinatore' || user?.role === 'responsabile') && location.pathname !== '/setup-2fa') {
    return <Navigate to="/setup-2fa" replace />
  }

  return children
}

/**
 * Coordinatore only route
 */
function CoordinatoreRoute({ children }) {
  const { isCoordinatore } = useAuth()

  if (!isCoordinatore()) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

/**
 * FASE 5: Responsabile only route
 */
function ResponsabileRoute({ children }) {
  const { isResponsabile } = useAuth()

  if (!isResponsabile()) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

/**
 * FIX: Route per Coordinatore/Educatore only
 * Blocca l'accesso ai Responsabili e li redirige a /organization
 */
function TenantUserRoute({ children }) {
  const { isResponsabile } = useAuth()

  // Se è un Responsabile, redirect alla sua dashboard
  if (isResponsabile()) {
    return <Navigate to="/organization" replace />
  }

  return children
}

/**
 * FIX: Smart Dashboard Redirect
 * Redirige alla dashboard corretta in base al ruolo
 */
function DashboardRedirect() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  // Responsabile → /organization
  if (user?.role === 'responsabile') {
    return <Navigate to="/organization" replace />
  }

  // Coordinatore/Educatore → /dashboard
  return <Navigate to="/dashboard" replace />
}

/**
 * Route per setup 2FA obbligatorio
 * Accessibile solo a coordinatori/responsabili che devono configurare 2FA
 */
function Setup2FARoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Solo coordinatori e responsabili possono accedere a questa pagina
  if (user?.role !== 'coordinatore' && user?.role !== 'responsabile') {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/resend-verification" element={<ResendVerificationPage />} />
      
      {/* 2FA routes */}
      <Route path="/verify-2fa" element={<TwoFactorVerifyPage />} />
      <Route path="/2fa/recovery/:token" element={<TwoFactorRecoveryPage />} />
      <Route path="/2fa/reset/:token" element={<TwoFactorRecoveryPage />} />
      
      {/* Setup 2FA obbligatorio */}
      <Route path="/setup-2fa" element={
        <Setup2FARoute>
          <TwoFactorSetupPage />
        </Setup2FARoute>
      } />
      
      {/* FASE 3: Legal Documents Sign */}
      <Route path="/legal/sign/:id" element={
        <ProtectedRoute>
          <LegalSignPage />
        </ProtectedRoute>
      } />
      
      {/* =========================================
          FASE 5: Routes Organization (Responsabile)
          ========================================= */}
      <Route
        path="/organization"
        element={
          <ProtectedRoute>
            <ResponsabileRoute>
              <NotificationFeedProvider>
                <AppLayout />
              </NotificationFeedProvider>
            </ResponsabileRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<ResponsabileDashboard />} />
        <Route path="dashboard" element={<ResponsabileDashboard />} />
        <Route path="centers" element={<CentersPage />} />
        <Route path="centers/new" element={<CreateCenterPage />} />
        <Route path="centers/:id" element={<CenterDetailPage />} />
        <Route path="centers/:id/edit" element={<CenterDetailPage />} />
        <Route path="settings" element={<OrganizationSettingsPage />} />
        <Route path="contracts" element={<OrganizationContracts />} />
        <Route path="billing" element={<OrganizationBilling />} />
        <Route path="export" element={<OrganizationExport />} />
        <Route path="profile" element={<ProfilePage />} />
        {/* Centro Notifiche + Chat + Reminder (anche per Responsabile) */}
        <Route path="notifiche" element={<NotificationsPage />} />
        <Route path="messaggi" element={<MessagesPage />} />
        <Route path="messaggi/:threadId" element={<MessagesPage />} />
        <Route path="promemoria" element={<RemindersPage />} />
        {/* Backward compat: actionUrl notifiche reminder è /promemoria/<id>,
            la route deve esistere altrimenti finisce nel catch-all → dashboard */}
        <Route path="promemoria/:id" element={<RemindersPage />} />
      </Route>
      {/* ========================================= */}
      
      {/* Protected routes (Coordinatore/Educatore) */}
      <Route path="/" element={
        <ProtectedRoute>
          <TenantUserRoute>
            <NotificationFeedProvider>
              <AppLayout />
            </NotificationFeedProvider>
          </TenantUserRoute>
        </ProtectedRoute>
      }>
        {/* FIX: Smart redirect basato sul ruolo */}
        <Route index element={
          <ProtectedRoute>
            <DashboardRedirect />
          </ProtectedRoute>
        } />
        
        {/* Dashboard - Solo Coordinatore/Educatore (TenantUserRoute già applicato sopra) */}
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Minori */}
        <Route path="minori" element={<MinoriPage />} />
        <Route path="minori/new" element={
          <CoordinatoreRoute>
            <NewMinorePage />
          </CoordinatoreRoute>
        } />
        <Route path="minori/:id" element={<MinoreDetailPage />} />
        <Route path="minori/:id/edit" element={
          <CoordinatoreRoute>
            <NewMinorePage />
          </CoordinatoreRoute>
        } />
        
        {/* Entries */}
        <Route path="entries" element={<EntriesPage />} />
        <Route path="entries/new" element={<NewEntryPage />} />
        <Route path="entries/new/:minoreId" element={<NewEntryPage />} />
        <Route path="entries/:id/edit" element={<NewEntryPage />} />
        
        {/* Quick Check-in */}
        <Route path="checkin" element={<CheckinPage />} />

        {/* Attività di Gruppo */}
        <Route path="group-activity" element={<GroupActivityPage />} />

        {/* Cestino */}
        <Route path="trash" element={<TrashPage />} />
        
        {/* Users (solo coordinatore) */}
        <Route path="users" element={
          <CoordinatoreRoute>
            <UsersPage />
          </CoordinatoreRoute>
        } />
        
        {/* Settings (solo coordinatore) */}
        <Route path="settings" element={
          <CoordinatoreRoute>
            <SettingsPage />
          </CoordinatoreRoute>
        } />
        
        {/* Reports */}
        <Route path="reports" element={<ReportsPage />} />
        
        {/* Template Builder (solo coordinatore) */}
        <Route path="reports/template/new" element={
          <CoordinatoreRoute>
            <TemplateBuilderPage />
          </CoordinatoreRoute>
        } />
        <Route path="reports/template/:templateId" element={
          <CoordinatoreRoute>
            <TemplateBuilderPage />
          </CoordinatoreRoute>
        } />
        
        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />
        
        {/* Subscription (solo coordinatore) */}
        <Route path="subscription" element={
          <CoordinatoreRoute>
            <SubscriptionPage />
          </CoordinatoreRoute>
        } />

        {/* Centro Notifiche + Chat + Reminder */}
        <Route path="notifiche" element={<NotificationsPage />} />
        <Route path="messaggi" element={<MessagesPage />} />
        <Route path="messaggi/:threadId" element={<MessagesPage />} />
        <Route path="promemoria" element={<RemindersPage />} />
        {/* Backward compat: actionUrl notifiche reminder è /promemoria/<id>,
            la route deve esistere altrimenti finisce nel catch-all → dashboard */}
        <Route path="promemoria/:id" element={<RemindersPage />} />
      </Route>

      {/* 404 - redirect to smart dashboard */}
      <Route path="*" element={
        <ProtectedRoute>
          <DashboardRedirect />
        </ProtectedRoute>
      } />
    </Routes>
  )
}
